import React from "react";
import { motion } from "framer-motion";
import { Upload, Layout, Send } from "lucide-react";
import Image from "next/image";
import dynamic from "next/dynamic";
import shareAndGetOrdersAnimation from "@/assets/lottie/share-and-get-orders.json";
import { useTranslation } from "react-i18next";

const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

export default function MenoSteps() {
  const { t } = useTranslation();

  const steps = [
    {
      id: 1,
      icon: <Upload aria-hidden="true" className="w-6 h-6 text-teal-500" />,
      iconBg: "bg-teal-50",
      headline: t("menoSteps.steps.s1.headline"),
      subheading: t("menoSteps.steps.s1.subheading"),
      bullets: t("menoSteps.steps.s1.bullets", {
        returnObjects: true,
      }) as string[],
      visual: (
        <div className="relative w-full bg-gradient-to-b from-white to-[#f8fafc] rounded-3xl flex items-center justify-center overflow-hidden">
          <div className="flex flex-col gap-3">
            <Image
              src="/images/meno/upload-your-catalog.webp"
              alt={t("menoSteps.steps.s1.imageAlt")}
              width={500}
              height={500}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      ),
      reverse: false,
    },
    {
      id: 2,
      icon: <Layout aria-hidden="true" className="w-6 h-6 text-purple-500" />,
      iconBg: "bg-purple-50",
      headline: t("menoSteps.steps.s2.headline"),
      subheading: t("menoSteps.steps.s2.subheading"),
      bullets: t("menoSteps.steps.s2.bullets", {
        returnObjects: true,
      }) as string[],
      visual: (
        <div className="relative w-full bg-gradient-to-b from-white to-[#f8fafc] rounded-3xl flex items-center justify-center">
          <Image
            src="/images/meno/customize-meno.webp"
            alt={t("menoSteps.steps.s2.imageAlt")}
            width={500}
            height={500}
            className="w-full h-full object-contain"
          />
        </div>
      ),
      reverse: true,
    },
    {
      id: 3,
      icon: <Send aria-hidden="true" className="w-6 h-6 text-green-500" />,
      iconBg: "bg-green-50",
      headline: t("menoSteps.steps.s3.headline"),
      subheading: t("menoSteps.steps.s3.subheading"),
      bullets: t("menoSteps.steps.s3.bullets", {
        returnObjects: true,
      }) as string[],
      visual: (
        <div className="relative w-full bg-gradient-to-b from-white to-[#f8fafc] rounded-3xl flex items-center justify-center">
          <motion.div className="flex flex-col gap-3">
            <Lottie
              animationData={shareAndGetOrdersAnimation}
              loop
              autoplay
              className="w-full h-full object-contain"
            />
          </motion.div>
        </div>
      ),
      reverse: false,
    },
  ];

  return (
    <section className="py-24 font-sans text-slate-900 safari-paint-contain">
      <div className="custom-container">
        <div className="text-center mb-20">
          <h2>
            <span
              className="text-4xl sm:text-5xl lg:text-6xl font-bold"
              style={{
                fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
              }}
            >
              {t("menoSteps.title1")}{" "}
              <span className="text-meno-orange">{t("menoSteps.title2")}</span>
            </span>
          </h2>
          <div
            className="text-slate-500 mt-3"
            style={{
              fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
            }}
          >
            {t("menoSteps.sub")}
          </div>
        </div>

        <div className="space-y-32">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`flex flex-col ${step.reverse ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-12 lg:gap-24`}
            >
              {/* Text Side */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-4">
                  <div
                    className={`${step.iconBg} w-12 h-12 rounded-lg flex items-center justify-center`}
                  >
                    {step.icon}
                  </div>
                  <div>
                    <h3
                      className="text-3xl font-bold mb-2"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {step.headline}
                    </h3>
                    <p
                      className="text-xl text-slate-600 font-medium"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {step.subheading}
                    </p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {step.bullets.map((bullet, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-slate-500"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Visual Side */}
              <div className="flex-1 w-full">{step.visual}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
